import {ChangeDetectionStrategy, Component, Inject, Input} from "@angular/core";
import {PendingField} from "../../../models/pending-field.model";
import {DraftJobChangeField} from "../../../types/draft-job-change-field.enum";
import {BringozDatePipe} from "../../../../commons/angular/pipes/bringoz-date.pipe";
import {BringozTimeConvertPipe} from "../../../../commons/angular/pipes/bringoz-time-convert.pipe";

const DATE_FIELDS = new Set([
  DraftJobChangeField.SLA,
  DraftJobChangeField.NOT_BEFORE,
  DraftJobChangeField.NOT_AFTER
]);

// Package (bringoz-server) sub-field -> existing flat translation key, reused as-is rather than
// adding a new nested namespace. Anything not listed here (shelfId, requiredSpots, nmfcCode,
// nmfcClass, numberOfItems, type, items, configuredFields, isCountUpdated, isWeightUpdated) falls
// back to its raw property name — rare enough to differ that a dedicated label isn't worth it.
const PACKAGE_FIELD_LABEL_KEYS: { [key: string]: string } = {
  barcode: 'Barcode',
  description: 'Description',
  sku: 'SKU',
  count: 'Count',
  volume: 'Volume',
  weight: 'Weight',
  pallets: 'Pallets',
  packsInItem: 'Packs In Item',
  value: 'Value',
  labels: 'Labels',
  size: 'Size',
  dimensions: 'Dimensions'
};

export interface PackageFieldDiff {
  label: string;
  oldValue: string;
  newValue: string;
}

require('./pending-field-row.scss');
@Component({
  standalone: false,
  selector: 'app-pending-field-row',
  templateUrl: './pending-field-row.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BringozDatePipe, BringozTimeConvertPipe]
})
export class PendingFieldRowComponent {
  @Input() field: DraftJobChangeField;
  @Input() pendingField: PendingField<any>;
  @Input() blocked: boolean;

  public readonly packagesField = DraftJobChangeField.PACKAGES;

  constructor(@Inject(BringozDatePipe) private bringozDatePipe: BringozDatePipe,
              @Inject(BringozTimeConvertPipe) private bringozTimeConvertPipe: BringozTimeConvertPipe) {
  }

  // `adjusted` (a planner edit from the underlying Field abstraction) takes precedence over
  // `initial` when present, matching visit-plan-tracker-changes-view's handling of the same type.
  get initialValue(): any {
    return this.pendingField?.adjusted?.value ?? this.pendingField?.initial?.value;
  }

  get pendingValue(): any {
    return this.pendingField?.pending?.value;
  }

  // Package (bringoz-server) has no single display label, so an "updated" package is shown as the
  // list of sub-fields that actually changed, each with its own old/new value, instead of a single
  // fallback label per side.
  get packageFieldDiffs(): PackageFieldDiff[] {
    const initial = this.initialValue;
    const pending = this.pendingValue;
    if (this.field !== DraftJobChangeField.PACKAGES ||
      !initial || typeof initial !== 'object' || !pending || typeof pending !== 'object') {
      return [];
    }

    const keys = new Set([...Object.keys(initial), ...Object.keys(pending)]);
    keys.delete('id');

    const diffs: PackageFieldDiff[] = [];
    keys.forEach((key) => {
      const oldValue = initial[key];
      const newValue = pending[key];
      if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
        return;
      }
      diffs.push({
        label: PACKAGE_FIELD_LABEL_KEYS[key] || key,
        oldValue: this.formatValue(oldValue),
        newValue: this.formatValue(newValue)
      });
    });
    return diffs;
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (DATE_FIELDS.has(this.field)) {
      return this.bringozDatePipe.transform({date: value}, 'date', true);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : '-';
    }
    if (this.field === DraftJobChangeField.ADDRESS && typeof value === 'object') {
      // PlanningLocation (bringoz-server) has no formatted-string field on the wire
      // (getFormattedString() is @JsonIgnore'd) — build one from its raw address parts.
      const parts = [value.house, value.street, value.city, value.state, value.zipCode, value.country];
      return parts.filter((part) => !!part).join(' ') || '-';
    }
    if (this.field === DraftJobChangeField.DETAILED_SERVICE_TIME && typeof value === 'object') {
      // ServiceTimePojo (bringoz-server): entryTime/exitTime/workTime/packagesTime/subTasksTime,
      // each in milliseconds — sum them into one readable duration.
      const totalMillis = (value.entryTime || 0) + (value.exitTime || 0) + (value.workTime || 0) +
        (value.packagesTime || 0) + (value.subTasksTime || 0);
      return this.bringozTimeConvertPipe.transform(totalMillis, true);
    }
    if (typeof value === 'object') {
      // Skill objects etc. — no canonical display model, fall back to a readable dump
      // rather than guessing a shape (see plan's Open Questions on ResourceSkill display).
      return value.name || JSON.stringify(value);
    }
    return String(value);
  }
}
